import type { CSSProperties } from 'vue'
import type { BlockBackgroundSettings, BlockMediaSettings } from './types'

export interface BlockBackgroundPreset {
  id: string
  name: string
  description: string
  type: 'solid' | 'gradient' | 'glow'
}

const background = 'var(--site-color-background, #f8f8f5)'
const surface = 'var(--site-color-surface, #ffffff)'
const primary = 'var(--site-color-primary, #6756FF)'
const accent = 'var(--site-color-accent, #D6D9FF)'

export const blockBackgroundPresets: BlockBackgroundPreset[] = [
  { id: 'canvas', name: 'Canvas', description: 'The theme\'s natural page background.', type: 'solid' },
  { id: 'surface', name: 'Soft Surface', description: 'A quiet raised surface for visual separation.', type: 'solid' },
  { id: 'primary-tint', name: 'Primary Tint', description: 'A restrained wash of the theme\'s primary color.', type: 'solid' },
  { id: 'accent-tint', name: 'Accent Tint', description: 'A gentle supporting color from the active theme.', type: 'solid' },
  { id: 'vertical-wash', name: 'Vertical Wash', description: 'A calm top-to-bottom surface transition.', type: 'gradient' },
  { id: 'horizontal-wash', name: 'Horizontal Wash', description: 'A soft accent moving across the section.', type: 'gradient' },
  { id: 'diagonal-accent', name: 'Diagonal Accent', description: 'A subtle directional theme gradient.', type: 'gradient' },
  { id: 'center-fade', name: 'Center Fade', description: 'A softly illuminated center with quiet edges.', type: 'gradient' },
  { id: 'edge-fade', name: 'Edge Fade', description: 'A light center framed by gentle side color.', type: 'gradient' },
  { id: 'center', name: 'Center Light', description: 'Ambient light focused behind the content.', type: 'glow' },
  { id: 'top-left', name: 'Top Left', description: 'Light entering softly from the upper left.', type: 'glow' },
  { id: 'top-right', name: 'Top Right', description: 'Light entering softly from the upper right.', type: 'glow' },
  { id: 'bottom-left', name: 'Bottom Left', description: 'A low glow anchored to the left.', type: 'glow' },
  { id: 'bottom-right', name: 'Bottom Right', description: 'A low glow anchored to the right.', type: 'glow' },
  { id: 'all-corners', name: 'All Corners', description: 'Balanced ambient color around every corner.', type: 'glow' },
  { id: 'side-edges', name: 'Side Edges', description: 'Soft light from both horizontal edges.', type: 'glow' },
  { id: 'vertical-edges', name: 'Vertical Edges', description: 'Light from the top and bottom edges.', type: 'glow' },
  { id: 'top-edge', name: 'Top Edge', description: 'A broad glow along the upper edge.', type: 'glow' },
  { id: 'bottom-edge', name: 'Bottom Edge', description: 'A broad glow along the lower edge.', type: 'glow' },
]

const solidStyles: Record<string, CSSProperties> = {
  canvas: { backgroundColor: background },
  surface: { backgroundColor: surface },
  'primary-tint': { backgroundColor: `color-mix(in oklab, ${primary} 8%, ${background})` },
  'accent-tint': { backgroundColor: `color-mix(in oklab, ${accent} 34%, ${background})` },
}

const gradientStyles: Record<string, CSSProperties> = {
  'vertical-wash': { backgroundColor: background, backgroundImage: `linear-gradient(180deg, ${surface} 0%, ${background} 100%)` },
  'horizontal-wash': { backgroundColor: background, backgroundImage: `linear-gradient(90deg, ${background} 0%, color-mix(in oklab, ${accent} 38%, ${background}) 100%)` },
  'diagonal-accent': { backgroundColor: background, backgroundImage: `linear-gradient(135deg, ${background} 15%, color-mix(in oklab, ${primary} 10%, ${background}) 100%)` },
  'center-fade': { backgroundColor: background, backgroundImage: `radial-gradient(circle at 50% 50%, color-mix(in oklab, ${accent} 46%, ${surface}) 0%, ${background} 68%)` },
  'edge-fade': { backgroundColor: background, backgroundImage: `linear-gradient(90deg, color-mix(in oklab, ${primary} 8%, ${background}) 0%, ${surface} 50%, color-mix(in oklab, ${primary} 8%, ${background}) 100%)` },
}

function glowColor(intensity: BlockBackgroundSettings['intensity']): string {
  const amount = intensity === 'medium' ? 24 : 14
  return `color-mix(in oklab, ${primary} ${amount}%, transparent)`
}

function radial(position: string, color: string, size = '70% 75%'): string {
  return `radial-gradient(${size} at ${position}, ${color} 0%, transparent 72%)`
}

function glowStyle(preset: string, intensity: BlockBackgroundSettings['intensity']): CSSProperties {
  const color = glowColor(intensity)
  const positions: Record<string, string[]> = {
    center: ['50% 50%'],
    'top-left': ['0% 0%'],
    'top-right': ['100% 0%'],
    'bottom-left': ['0% 100%'],
    'bottom-right': ['100% 100%'],
    'all-corners': ['0% 0%', '100% 0%', '0% 100%', '100% 100%'],
    'side-edges': ['0% 50%', '100% 50%'],
    'vertical-edges': ['50% 0%', '50% 100%'],
    'top-edge': ['50% 0%'],
    'bottom-edge': ['50% 100%'],
  }

  return {
    backgroundColor: background,
    backgroundImage: (positions[preset] ?? positions.center!).map((position) => radial(position, color)).join(', '),
  }
}

function imageStyle(src: string): CSSProperties {
  const overlay = `color-mix(in oklab, ${background} 78%, transparent)`

  return {
    backgroundColor: background,
    backgroundImage: `linear-gradient(${overlay}, ${overlay}), url(${JSON.stringify(src)})`,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
  }
}

export function resolveBlockBackgroundStyle(settings: BlockMediaSettings): CSSProperties | undefined {
  const selection = settings.background

  if ((!selection || selection.type === 'image') && settings.backgroundImage?.src) {
    return imageStyle(settings.backgroundImage.src)
  }
  if (!selection || selection.type === 'default') return undefined
  if (selection.type === 'solid') return solidStyles[selection.preset ?? 'canvas'] ?? solidStyles.canvas
  if (selection.type === 'gradient') return gradientStyles[selection.preset ?? 'vertical-wash'] ?? gradientStyles['vertical-wash']
  if (selection.type === 'glow') return glowStyle(selection.preset ?? 'center', selection.intensity ?? 'subtle')

  return undefined
}

export function blockBackgroundName(settings: BlockMediaSettings): string {
  if (settings.background?.type === 'image' || (!settings.background && settings.backgroundImage)) return 'Image'
  if (!settings.background || settings.background.type === 'default') return 'Theme default'

  return blockBackgroundPresets.find((preset) => preset.id === settings.background?.preset)?.name ?? 'Theme default'
}
